import type { ConfigType } from '@plone/registry';
// Global design-token layer (single :root emit). Component .less files
// borrow the @pp-bp-* Less vars via `@import (reference)`; this line is
// what actually outputs the CSS custom properties on :root, once.
import './theme/tokens.less';
import './theme/components.less';
import './theme/search.less';
import './theme/page.less';
import './theme/searchApp.less';
import './theme/browse.less';
import './theme/relations.less';
import './theme/familyTimeline.less';
import './theme/changes.less';
import './theme/fulltext.less';
// Authoritative design system (design tokens + kit.css + prc-* component CSS)
// and its Volto/Semantic-UI integration layer. Loaded last so the design
// ground + chrome win over Volto/Semantic defaults.
import './theme/ds-core.css';
import './theme/ds-volto.css';
// Deliberately AFTER the design system: the result-row additions (matched-text
// snippet, cross-archive badge) have to beat .prc-table's own zebra + cell
// rules at equal specificity, and source order is the honest way to do it.
import './theme/resultRow.less';
import './theme/suggest.less';
import './theme/advanced.less';
// Last: the error view restyles nothing it does not own, but its link
// and card rules sit at the same specificity as ds-volto.css defaults.
import './theme/errors.less';
// The privacy-choices banner + footer consent links (BL-0079). Not part of
// the ported design system, so it is its own file rather than ds-core.css.
import './theme/consent.less';
// The contact form (BL-0096). Same reasoning as consent.less: not part of the
// ported design system, and its controls have to beat ds-volto.css's
// Semantic-UI input defaults at equal specificity.
import './theme/contact.less';
// The homepage hero's second paragraph (BL-0112). Same reasoning as the two
// above, and it must stay after ds-core.css -- see the file's own header.
import './theme/siteHome.less';
// The affiliation notice above the masthead (BL-0114). Same reasoning again,
// and it must stay after ds-core.css -- see the file's own header.
import './theme/affiliationNotice.less';
// LAST, and it has to stay last: the accessibility corrections are
// expressed as later-source-order overrides of ds-core.css, which is the
// ported design system and is not ours to edit. See the file's header.
import './theme/a11y.less';
import installSettings from './config/settings';
import getSearchBlockAsyncData from './components/Browse/getSearchBlockAsyncData';
import querystringsearchKeepingStale from './reducers/querystringsearch';
import suggest from './reducers/suggest';
import site from './reducers/site';
import familyTimeline from './reducers/familyTimeline';
import changes from './reducers/changes';
import Changes from './components/Changes/Changes';
import FamilyTimeline from './components/FamilyTimeline/FamilyTimeline';
import { HISTORY_SEGMENT } from './components/FamilyTimeline/url';
import DocumentView from './components/DocumentView/DocumentView';
import FileDetailView from './components/DocumentView/FileDetailView';
import NewsItemView from './components/NewsView/NewsItemView';
import SiteTheme from './components/SiteTheme/SiteTheme';
import SiteAnalytics from './components/SiteAnalytics/SiteAnalytics';
import FeedDiscovery from './components/SEO/FeedDiscovery';
import BreadcrumbJsonLd from './components/SEO/BreadcrumbJsonLd';
import CookieNotice from './components/CookieNotice/CookieNotice';
import PrivacyNotice from './components/PrivacyNotice/PrivacyNotice';
import ContactForm from './components/ContactForm/ContactForm';
import RouteTrail from './components/ContactForm/RouteTrail';
import UnauthorizedView from './components/Errors/UnauthorizedView';
import NotFoundView from './components/Errors/NotFoundView';
import NoIndexRoutes from './components/SEO/NoIndexRoutes';
import TableListingView from './components/listingVariations/TableListing';
import SearchNoResults from './components/Search/SearchNoResults';
import NewsListingView from './components/listingVariations/NewsListing';
import withNormalizedData from './components/withNormalizedData';
import withPresetData from './components/withPresetData';
import BlocksAwareListingView from './components/BlocksAwareListingView';
import SiteHome from './components/SiteHome/SiteHome';
import { homeAsyncPropExtender } from './components/SiteHome/asyncData';
import { dedupeSsrStateExtender } from './config/dedupeSsrState';
import CheckboxFacet from '@plone/volto/components/manage/Blocks/Search/components/CheckboxFacet';
import SelectFacetFilterListEntry from '@plone/volto/components/manage/Blocks/Search/components/SelectFacetFilterListEntry';
import { selectFacetValueToQuery } from '@plone/volto/components/manage/Blocks/Search/components/base';
import { SITE_CHOICES } from './config/searchApp';

/**
 * Portal types that get the shared document detail view. This is view
 * REGISTRATION (inherently build-time in Volto), not display config — which
 * fields each type shows comes from the backend `document_display` expander
 * (schema-driven), never from a frontend list.
 */
const DOCUMENT_TYPES = [
  'Standard',
  'Directive',
  'OtherPolicy',
  'Policy',
  'Delegation',
  'Definition',
  'Acronym',
  'Charter',
  'Person',
  'Project',
];

/**
 * @dox/policy-portal — the single shared codebase for the three archives
 * (standards / directives / nnsa). Per-site differences live in ./siteConfig
 * and are resolved at runtime; everything else is shared here.
 */
function applyConfig(config: ConfigType) {
  installSettings(config);

  // One shared detail view for every archive document type. Portal types are
  // globally unique across the three sites, so this registration is unambiguous.
  DOCUMENT_TYPES.forEach((type) => {
    config.views.contentTypesViews[type] = DocumentView;
  });

  // `File` is NOT in DOCUMENT_TYPES: it's a generic type used far beyond the
  // reviewed change-charts/admin-change memos (BL-0032), so it gets a
  // container-aware wrapper instead of an unconditional DocumentView swap --
  // see FileDetailView for the files/related-items scoping this mirrors from
  // the backend reconciler.
  config.views.contentTypesViews['File'] = FileDetailView;

  // News Item detail view: core's own NewsItemView, plus the `<title>`
  // suffix fix documents already carry (see components/SEO/NewsSEO — a
  // returning-visitor fix, part of BL-0099). Core's contentTypesViews
  // registers 'News Item' -> its NewsItemView in config/Views.jsx; this
  // overrides that same key rather than shadowing the ~50-line core
  // component, matching how FileDetailView above overrides 'File'.
  config.views.contentTypesViews['News Item'] = NewsItemView;

  // Finding-tool home view for the site root (@type 'Plone Site').
  config.views.contentTypesViews['Plone Site'] = SiteHome;

  // `/<document>/version-history` — the version-family hub. A REAL route, not
  // a content view: it is the one page in the archive that belongs to a family
  // rather than to a document, and being a route is also what lets it load its
  // own data on the server (`asyncConnect`), which a content view cannot do.
  // Addon routes are matched ahead of core's `/**` catch-all, so this wins
  // over the content View for this suffix and nothing else.
  config.addonRoutes = [
    ...(config.addonRoutes || []),
    { path: `/**/${HISTORY_SEGMENT}`, component: FamilyTimeline },
    // `/privacy` — the notice BL-0079 requires; see PrivacyNotice for why it
    // is a route rather than Plone content (no such object exists on any of
    // the three sites, so the content fetch would 404 without this).
    { path: '/privacy', component: PrivacyNotice, exact: true },
    // `/contact` — the one contact page (BL-0096), a route for the same
    // reason /privacy is one: no `contact` object exists on any of the three
    // sites, so the backend content fetch would 404.
    { path: '/contact', component: ContactForm, exact: true },
    // `/changes` - the newest documents by DOE issue date, a catalog search
    // (BL-0129). A route for the same reason /privacy is one.
    { path: '/changes', component: Changes, exact: true },
  ];

  // Ship the schema-derived display rows with every content GET (SSR included)
  // so the detail view renders without a frontend field list.
  config.settings.apiExpanders = [
    ...(config.settings.apiExpanders || []),
    { match: '', GET_CONTENT: ['document_display', 'document_relations'] },
  ];

  // SSR the home page's recent-docs/counts data. SiteHome is resolved inside
  // the generic content View (not its own react-router route), so a per-view
  // asyncConnect can't reach it — asyncPropsExtenders fire on every route
  // match, server and client. See components/SiteHome/asyncData.js.
  config.settings.asyncPropsExtenders = [
    ...(config.settings.asyncPropsExtenders || []),
    homeAsyncPropExtender,
  ];

  // BL-0153: drop the two reduxAsyncConnect keys that only ever duplicate
  // another reducer's slice (GET_SITE == site.data; content == content.data
  // on a Document) out of the SSR payload before it is serialized into
  // window.__data. See config/dedupeSsrState.ts for why this is safe and why
  // it is a storeExtenders middleware rather than a change to core.
  config.settings.storeExtenders = [
    ...(config.settings.storeExtenders || []),
    dedupeSsrStateExtender,
  ];

  // Migrated Folders/Collections keep FTI layout listing_view, which would
  // hide their authored blocks; render blocks when present.
  config.views.layoutViews.listing_view = BlocksAwareListingView;

  // Anonymous requests for private content answer 410 Gone instead of
  // redirecting to an indexable login page (BL-0041). Registered as a view
  // rather than shadowing core's Unauthorized, because the CMS-UI imports that
  // component directly in six places where the login redirect is wanted.
  config.views.errorViews[401] = UnauthorizedView;
  // Also the fallback for every status with no view of its own (core
  // Error.jsx), which is why NotFoundView reads the real status rather
  // than assuming 404. Replaces a stock page whose only link
  // (/contact-form) is a dead end here and which emits no <title> at all.
  config.views.errorViews[404] = NotFoundView;

  // Inject the active site's brand palette (CSS custom properties) on every
  // page — this is how three sites keep distinct colours from one build.
  config.settings.appExtras = [
    ...(config.settings.appExtras || []),
    { match: '', component: SiteTheme },
    { match: '', component: SiteAnalytics },
    // Emits meta robots noindex on login/logout/CMS routes. Decides from the
    // pathname itself, so `match: ''` is correct — see the component.
    { match: '', component: NoIndexRoutes },
    // Sitewide RSS feed-discovery <link> for /changes/rss (BL-0129), a
    // returning-visitor fix (BL-0099). `match: ''`: a per-site fact ("this
    // archive has a changes feed"), not a per-document one, so it belongs on
    // every page the same way SiteTheme's brand palette does. See the
    // component for the curl evidence that no page carried it.
    { match: '', component: FeedDiscovery },
    // schema.org BreadcrumbList from the same `state.breadcrumbs` the visible
    // trail renders (item 7). Emits nothing where there is no trail (the site
    // root), so `match: ''` is correct -- see the component.
    { match: '', component: BreadcrumbJsonLd },
    // The privacy-choices banner (BL-0079). Renders null server-side and on
    // first client render — see the component for why `match: ''` is safe
    // despite that (O13: SSR output never depends on consent state).
    { match: '', component: CookieNotice },
    // Remembers the previous route in sessionStorage so a "report a problem"
    // link from anywhere knows where the visitor came from. Renders null and
    // touches nothing server-side — `document.referrer` is useless in an SPA
    // (it reports the original entry page), which is why this exists.
    { match: '', component: RouteTrail },
  ];

  // Document-list templates. `listingBodyTemplate` on the migrated search blocks
  // (and `variation` on listing blocks) resolve against the listing variations,
  // so the tabular "cards-id" and dated "news-view-1" views live here and serve
  // both block types across all three archives.
  const listing: any = config.blocks.blocksConfig.listing;
  const variations = listing.variations || (listing.variations = []);
  if (!variations.find((v: any) => v.id === 'cards-id')) {
    variations.push({
      id: 'cards-id',
      title: 'Document table',
      template: TableListingView,
      // An empty result set is a diagnosis, not a sentence — the panel reads
      // `search_meta` and names the filter that emptied the page (item 18).
      // ListingBody prefers a variation's own noResultsComponent over the
      // listing block's global one, so this covers the search app and every
      // browse listing that uses this variation.
      noResultsComponent: SearchNoResults,
    });
  }
  if (!variations.find((v: any) => v.id === 'news-view-1')) {
    variations.push({
      id: 'news-view-1',
      title: 'News list',
      template: NewsListingView,
    });
  }

  // Normalize migrated block data (legacy field/index/portal_type names) onto
  // the unified schema at render time, for both the search and listing blocks.
  const search: any = config.blocks.blocksConfig.search;

  // Cross-site "Archive" facet. A custom facet widget type whose choices are
  // static / config-driven (the backend has no `site` catalog index, so Volto
  // cannot source them from a querystring vocabulary). Selecting archives adds
  // a `site` criterion that the Solr seam turns into an fq; an empty selection
  // means the current site only (the seam's default scope). `showFacet: true`
  // makes it render despite the empty querystring index. See searchApp.ts
  // SITE_CHOICES + backend search/config.py SITE_BASE_URLS / SITE_LABELS.
  const fw: any = search.extensions?.facetWidgets;
  if (fw) {
    const SITE_LABEL: Record<string, string> = SITE_CHOICES.reduce(
      (m: Record<string, string>, c) => {
        m[c.value] = c.label;
        return m;
      },
      {},
    );
    if (!fw.types.find((t: any) => t.id === 'archiveFacet')) {
      fw.types.push({
        id: 'archiveFacet',
        title: 'Archive',
        view: CheckboxFacet,
        showFacet: () => true,
        stateToValue: ({ facetSettings, selectedValue }: any) => {
          const isMulti = facetSettings.multiple;
          if (!selectedValue) return isMulti ? [] : null;
          const mk = (v: string) => ({ value: v, label: SITE_LABEL[v] || v });
          return isMulti
            ? (Array.isArray(selectedValue)
                ? selectedValue
                : [selectedValue]
              ).map(mk)
            : mk(selectedValue);
        },
        valueToQuery: selectFacetValueToQuery,
        filterListComponent: SelectFacetFilterListEntry,
      });
    }
    const prevRewrite = fw.rewriteOptions;
    fw.rewriteOptions = (name: string, choices: any) =>
      name === 'site'
        ? SITE_CHOICES.map((c) => ({ value: c.value, label: c.label }))
        : prevRewrite
          ? prevRewrite(name, choices)
          : choices;
  }
  // Server-render the search block's first page of results. Core SSRs the
  // listing block and not this one, which left every browse container and every
  // `?preset=` category page rendering zero document links — no internal link
  // graph at all, on any of the three archives. See the module docstring.
  search.getAsyncData = getSearchBlockAsyncData;

  if (search.view && !search.view.__ppPreset) {
    // preset merge composes OUTERMOST so ?preset= lands before withSearch seeds
    search.view = withPresetData(withNormalizedData(search.view));
  }
  if (listing.view && !listing.view.__ppNormalized) {
    listing.view = withNormalizedData(listing.view);
  }

  // Keep the previous rows on screen while a search refetch is in flight.
  // Core blanks them on _PENDING, which was invisible while nothing was
  // server-rendered and is a visible flash now that the first page arrives in
  // the HTML. store.js spreads addonReducers after core's, so this replaces it.
  config.addonReducers = {
    ...(config.addonReducers || {}),
    querystringsearch: querystringsearchKeepingStale,
    // Typeahead (item 3). A NEW slice, not a replacement — it holds only the
    // suggestion list, and it drops responses that arrive for a prefix the
    // user has already typed past. See reducers/suggest.js.
    suggest,
    // The version-family hub's own slice. Fetched per route by asyncConnect,
    // so it is populated on the SERVER before the HTML is rendered.
    familyTimeline,
    // `/changes` (BL-0129), fetched on the server by asyncConnect.
    changes,
    // BL-0157 (TAIUIT scan 2026-09-29, Error Page Disclosure): core's `site`
    // slice stores the whole @site response, including the ~7.8 KB
    // `plone.robots_txt`, and it is serialized into window.__data on every
    // page. Nothing reads it from the store; see reducers/site.js.
    site,
  };

  // `/sitemap-browse.xml` — the `?preset=` category pages are not content
  // objects, so core's `@search`-derived sitemap cannot see them. Server-only:
  // the module pulls in express, and the __SERVER__ constant lets webpack drop
  // it from the client bundle (the same idiom core uses for config/server.js).
  if (typeof __SERVER__ !== 'undefined' && __SERVER__) {
    const browseSitemapMiddleware = require('./express/sitemapBrowse').default;
    const downloadCanonicalMiddleware =
      require('./express/downloadCanonical').default;
    // `/llms.txt` — the archive described for machines that read prose, and
    // the search API documented so an assistant can ask instead of scrape.
    // Generated from the same registry the app and the eval harness read, so
    // it cannot drift from what is actually served.
    const llmsTxtMiddleware = require('./express/llmsTxt').default;
    // `/changes/rss` - the /changes page as a feed (BL-0129).
    const changesRssMiddleware = require('./express/changesRss').default;
    // Re-assert this instance's public origin over the value core's
    // setupServer derived from the request headers. setupServer is registered
    // as `server.all('*', ...)`, ahead of this array, and the render handler
    // runs after it — so this array is the only seam where the assignment
    // sticks. Must precede any middleware that emits absolute URLs (the two
    // sitemap handlers below both do). See express/publicOrigin.js.
    const publicOriginMiddleware = require('./express/publicOrigin').default;
    // BL-0048: carry the BACKEND's redirect status out to the client. Volto's
    // View.jsx collapses 301 and 302 into one `<Redirect>`, and core's
    // server.jsx then calls `res.redirect(url)`, which Express defaults to 302
    // — so all 16,524 permanent moves were answering "temporary" and passing
    // on no link authority. Must sit ahead of the render handler, which this
    // array is; it only wraps `res.redirect` and calls next().
    const permanentRedirectsMiddleware =
      require('./express/permanentRedirects').default;
    // Classic-shape URLs (`/@@search`, `<path>/view`) used to 500; the backend
    // guard now 404s them, and this answers 301 for the ones whose target is
    // still live. FIRST in the chain: it terminates the request outright, so
    // nothing below should do work for a URL that is only going to be
    // redirected. See express/legacySuffixRedirects.js.
    const legacySuffixRedirectsMiddleware =
      require('./express/legacySuffixRedirects').default;
    // `/contact-us` and `/contact-form` -> `/contact`, 301 (BL-0096). Both are
    // in the edge logs; neither is a content object, so there is nothing in
    // Plone to hang a redirector entry on. Terminates the request, so it sits
    // with the other terminating handler at the top.
    const aliasRedirectsMiddleware =
      require('./express/aliasRedirects').default;
    // `<doc>/%40%40download/file` -> 301 to the literal `@@` spelling, and
    // `<file>/@@download` (no field name) -> the backend's own answer. Both
    // used to miss core's files proxy, whose patterns match the raw path, and
    // 500 in the render. Terminates the request, so it sits with the other
    // terminating handlers at the top. See express/downloadPathGuard.js.
    const downloadPathGuardMiddleware =
      require('./express/downloadPathGuard').default;
    // `/sitemap/<anything>` -> 404, not the SSR 500 core's non-exact
    // `/sitemap` page route produces for it (Sitemap.jsx's asyncConnect calls
    // expandToBackendURL(undefined) when navroot can't resolve a bogus
    // sub-path). Nothing is ever meant to exist below `/sitemap/`, so this
    // terminates the request outright, like the other guards above. See
    // express/sitemapPathGuard.js for the full mechanism and evidence.
    const sitemapPathGuardMiddleware =
      require('./express/sitemapPathGuard').default;
    config.settings.expressMiddleware = [
      legacySuffixRedirectsMiddleware(),
      aliasRedirectsMiddleware(),
      downloadPathGuardMiddleware(),
      permanentRedirectsMiddleware(),
      publicOriginMiddleware(),
      // FIRST among the body-serving handlers: it only sets a header and calls
      // next(), and core's filesMiddleware — which sends the body — is already
      // in this array.
      downloadCanonicalMiddleware(),
      sitemapPathGuardMiddleware(),
      ...(config.settings.expressMiddleware || []),
      browseSitemapMiddleware(),
      llmsTxtMiddleware(),
      changesRssMiddleware(),
    ];
  }

  return config;
}

export default applyConfig;
